import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { selectIaLogic } from 'selectors/general';

import useModifierMutations from './useModifierMutations';
import useModifiersReadOnly from './useModifiersReadOnly';

export function useModifierAuthoringAccess() {
  const { isReadOnly } = useModifiersReadOnly();
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_MODIFIERS));

  return { canAuthorModifierLinks: !isReadOnly && featureIsSupported };
}

export default function useModifierItemLinks({ venueId }) {
  const { canAuthorModifierLinks } = useModifierAuthoringAccess();
  const { linkGroupToItems, unlinkGroupFromItem } = useModifierMutations({ venueId });

  const submitGroupLinksForItem = useCallback(
    async ({ itemId, linked = [], unlinked = [] }) => {
      if (!canAuthorModifierLinks || (linked.length === 0 && unlinked.length === 0)) return;

      const linkResults = await Promise.allSettled([
        ...linked.map(groupId => linkGroupToItems.mutateAsync({ groupId, itemIds: [itemId] })),
        ...unlinked.map(groupId => unlinkGroupFromItem.mutateAsync({ groupId, itemId })),
      ]);
      const linkFailures = linkResults.filter(result => result.status === 'rejected');
      if (linkFailures.length > 0) {
        reportError('Some modifier group links failed', { failures: linkFailures.map(failure => failure.reason) });
      }
    },
    [canAuthorModifierLinks, linkGroupToItems, unlinkGroupFromItem]
  );

  return { canAuthorModifierLinks, submitGroupLinksForItem };
}
